import React, { useEffect } from "react";

type ModalProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    /** Footer actions, right-aligned. The modal supplies no default buttons. */
    footer?: React.ReactNode;
    children: React.ReactNode;
};

/**
 * Centred dialog over a blurred scrim. Escape and the scrim both dismiss — anything destructive
 * must confirm through its own footer buttons, never rely on staying open.
 */
export const Modal: React.FC<ModalProps> = ({ open, onClose, title, subtitle, footer, children }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-40">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            />
            <div
                role="dialog"
                aria-label={title}
                className="absolute left-1/2 top-1/2 flex max-h-[86vh] w-[min(560px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-[var(--line-2)] bg-[var(--surface)] shadow-[0_40px_90px_rgba(0,0,0,.5)]"
            >
                <div className="px-6 pb-3 pt-6">
                    <div className="text-[18px] font-semibold tracking-tight">{title}</div>
                    {subtitle && <div className="mt-1 text-[12.5px] text-[var(--muted)]">{subtitle}</div>}
                </div>
                <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-6 py-1">{children}</div>
                <div className="flex justify-end gap-2.5 px-6 pb-6 pt-5">{footer}</div>
            </div>
        </div>
    );
};
